import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../api/axios';
import toast from 'react-hot-toast';

const PLAN_COLORS = {
  Free:     'border-gray-200 bg-white',
  Silver:   'border-gray-400 bg-white',
  Gold:     'border-yellow-400 bg-yellow-50',
  Platinum: 'border-primary bg-primary-50',
};

const PLAN_BADGES = {
  Gold:     'bg-gold text-white',
  Platinum: 'bg-primary text-white',
};

export default function SubscriptionPage() {
  const [plans,    setPlans]    = useState([]);
  const [current,  setCurrent]  = useState(null);
  const [cycle,    setCycle]    = useState('monthly');
  const [loading,  setLoading]  = useState(true);
  const [paying,   setPaying]   = useState(null);

  const loadPlansAndSub = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/my'),
      ]);
      setPlans(plansRes.data.plans.filter(p => p.name !== 'Free'));
      setCurrent(subRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadPlansAndSub();

    // PayU redirects the browser back here (not a JS callback like Razorpay),
    // so the result arrives as a query param instead of a handler function.
    const params = new URLSearchParams(window.location.search);
    const payuResult = params.get('payu');
    if (payuResult === 'success') {
      toast.success('🎉 Payment successful! Your plan is now active.');
      loadPlansAndSub();
      window.history.replaceState({}, '', '/subscription');
    } else if (payuResult === 'failed') {
      toast.error('Payment failed or was cancelled. Please try again.');
      window.history.replaceState({}, '', '/subscription');
    }
  }, []);

  // PayU's hosted checkout is a plain form POST, not a JS SDK — the browser
  // navigates away to PayU's page and PayU redirects back to /subscription
  // (handled by the useEffect above) once the customer finishes paying.
  const handlePayUSubscribe = async (plan) => {
    setPaying(`payu-${plan.id}`);
    try {
      const { data } = await api.post('/subscriptions/payu/create-order', {
        plan_id: plan.id,
        billing_cycle: cycle,
      });

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.action_url;

      const fields = {
        key: data.key,
        txnid: data.txnid,
        amount: data.amount,
        productinfo: data.productinfo,
        firstname: data.firstname,
        email: data.email,
        phone: data.phone,
        surl: data.surl,
        furl: data.furl,
        hash: data.hash,
      };
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value ?? '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate PayU payment');
      setPaying(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl font-bold text-primary mb-3">✨ Choose Your Plan</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Unlock full access to connect with your ideal partner. All plans include a 30-day free trial for new members.
          </p>

          {/* Cycle toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-xl p-1 mt-6">
            <button onClick={() => setCycle('monthly')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${cycle === 'monthly' ? 'bg-white shadow-sm text-primary' : 'text-gray-500'}`}>
              Monthly
            </button>
            <button onClick={() => setCycle('yearly')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${cycle === 'yearly' ? 'bg-white shadow-sm text-primary' : 'text-gray-500'}`}>
              Yearly <span className="text-green-500 text-xs font-bold ml-1">Save ~17%</span>
            </button>
          </div>
        </div>

        {/* Current subscription */}
        {current?.subscription && (
          <div className="card p-4 mb-8 border-green-200 bg-green-50 flex items-center gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <p className="font-semibold text-gray-900">
                Active: {current.subscription.plan_name} Plan
              </p>
              <p className="text-sm text-gray-500">
                Expires: {new Date(current.subscription.expires_at).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}
              </p>
            </div>
          </div>
        )}

        {current?.isOnTrial && (
          <div className="card p-4 mb-8 border-blue-200 bg-blue-50 flex items-center gap-3">
            <span className="text-2xl">🎁</span>
            <p className="text-sm text-gray-700">
              You're on a <strong>free trial</strong> until {new Date(current.trialEndsAt).toLocaleDateString('en-IN')}. Upgrade to continue enjoying premium features after trial ends.
            </p>
          </div>
        )}

        {/* Plans grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const price     = cycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
            const isPopular = plan.name === 'Gold';
            const isCurrent = current?.subscription?.plan_id === plan.id;

            return (
              <div key={plan.id}
                className={`relative card border-2 p-6 ${PLAN_COLORS[plan.name] || 'border-gray-200 bg-white'} ${isPopular ? 'scale-105 shadow-xl' : ''}`}>
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gold text-white text-xs font-bold px-5 py-1.5 rounded-full shadow">
                    MOST POPULAR
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-4 right-4 bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow">
                    CURRENT
                  </div>
                )}

                <h3 className="font-heading text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                <div className="flex items-end gap-1 mb-4">
                  <span className="text-4xl font-bold text-primary">₹{price?.toLocaleString('en-IN')}</span>
                  <span className="text-gray-400 text-sm pb-1">/{cycle === 'yearly' ? 'year' : 'month'}</span>
                </div>

                <ul className="space-y-2 mb-6">
                  {[
                    plan.contact_limit === -1 ? 'Unlimited contacts/month' : `${plan.contact_limit} contacts/month`,
                    plan.can_chat            ? '✅ Chat with matches'      : '❌ Chat not included',
                    plan.can_view_contact    ? '✅ View contact numbers'   : '❌ Contact numbers hidden',
                    plan.horoscope_match     ? '✅ Horoscope matching'     : null,
                    plan.priority_listing    ? '✅ Priority listing'       : null,
                    plan.ai_match_score      ? '✅ AI compatibility score' : null,
                  ].filter(Boolean).map(f => (
                    <li key={f} className="text-sm text-gray-600 flex items-center gap-2">
                      {f.startsWith('✅') || f.startsWith('❌') ? f : <><span className="text-green-500">✓</span>{f}</>}
                    </li>
                  ))}
                </ul>

                {plan.description && (
                  <p className="text-xs text-gray-400 mb-4 italic">{plan.description}</p>
                )}

                <button
                  onClick={() => handlePayUSubscribe(plan)}
                  disabled={isCurrent || paying === `payu-${plan.id}`}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 disabled:opacity-50 ${
                    isPopular ? 'btn-gold' : 'btn-primary'
                  }`}
                >
                  {paying === `payu-${plan.id}` ? 'Redirecting to PayU...' : isCurrent ? 'Current Plan' : `Pay with PayU — ₹${price?.toLocaleString('en-IN')}`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Features comparison */}
        <div className="mt-16">
          <h2 className="font-heading text-2xl font-bold text-center text-gray-900 mb-8">Feature Comparison</h2>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="text-left p-4 font-medium">Feature</th>
                  <th className="text-center p-4 font-medium">Silver</th>
                  <th className="text-center p-4 font-medium">Gold</th>
                  <th className="text-center p-4 font-medium">Platinum</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Contacts per month', '10', '30', 'Unlimited'],
                  ['Chat with matches', '✅', '✅', '✅'],
                  ['View contact numbers', '✅', '✅', '✅'],
                  ['Horoscope matching', '❌', '✅', '✅'],
                  ['AI compatibility score', '❌', '✅', '✅'],
                  ['Priority listing', '❌', '❌', '✅'],
                  ['Dedicated support', '❌', '❌', '✅'],
                  ['Profile photos', '✅', '✅', '✅'],
                ].map(([feat, ...vals], i) => (
                  <tr key={feat} className={i % 2 === 0 ? 'bg-cream-100' : 'bg-white'}>
                    <td className="p-4 font-medium text-gray-700">{feat}</td>
                    {vals.map((v, vi) => <td key={vi} className="p-4 text-center text-gray-600">{v}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          All payments are secured via PayU. 100% safe & encrypted. <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 object-contain inline-block align-text-bottom" />
        </p>
      </div>
    </div>
  );
}
